import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { MessageSquareText, Loader2, CheckCircle2, XCircle, ShieldAlert } from "lucide-react";
import { Button } from "./ui/button";
import { smsConsentApi } from "../lib/api";

const STATUS_LABEL = {
  granted: { text: "Consented", tone: "green" },
  opted_in: { text: "Opted In", tone: "green" },
  declined: { text: "Declined", tone: "amber" },
  opted_out: { text: "Opted Out", tone: "red" },
};

/** Verbal SMS consent capture (transactional/service messaging only) — shown
 * on the customer profile so a dispatcher/tech can record the customer's
 * answer during a booking call. Never defaults to "Yes". */
export function SmsConsentWidget({ customerId }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    try {
      const res = await smsConsentApi.get(customerId);
      setData(res);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (customerId) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId]);

  const submit = async (granted) => {
    setSubmitting(true);
    try {
      await smsConsentApi.record(customerId, { method: "verbal", granted });
      toast.success(granted ? "SMS consent recorded" : "Recorded as declined — no automated SMS will be sent");
      setModalOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to record consent");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return null;

  const consent = data?.consent;
  const label = consent ? STATUS_LABEL[consent.consentStatus] : null;

  return (
    <div className="flex items-center gap-2" data-testid="sms-consent-widget">
      <MessageSquareText className="h-3.5 w-3.5 text-slate-400 shrink-0" />
      {label ? (
        <span
          data-testid="sms-consent-status"
          className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${
            label.tone === "green" ? "bg-emerald-100 text-emerald-700" : label.tone === "red" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"
          }`}
        >
          SMS: {label.text}
        </span>
      ) : (
        <span data-testid="sms-consent-status" className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">
          SMS: Not Captured
        </span>
      )}
      <button
        type="button"
        onClick={() => setModalOpen(true)}
        data-testid="sms-consent-record-btn"
        className="text-[11px] font-semibold text-blue-600 hover:underline"
      >
        Record Consent
      </button>

      {modalOpen && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-slate-900/40 px-4" data-testid="sms-consent-modal">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-5">
            <div className="flex items-center gap-2 mb-3">
              <ShieldAlert className="h-4 w-4 text-blue-600" />
              <h3 className="text-[14px] font-bold text-slate-900">Record Verbal SMS Consent</h3>
            </div>
            <p className="text-[13px] text-slate-600 mb-4" data-testid="sms-consent-disclosure">
              {data?.disclosureVerbal ||
                "Would you like to receive text messages regarding your appointments and services? Message frequency varies. Message and data rates may apply. Reply STOP to opt out or HELP for help. Consent is not a condition of purchase. Do you agree to receive these text messages?"}
            </p>
            <p className="text-[11.5px] text-slate-400 mb-4">Read this disclosure to the customer, then record their answer.</p>
            <div className="flex justify-end gap-2">
              <Button variant="outline" disabled={submitting} onClick={() => setModalOpen(false)} data-testid="sms-consent-cancel-btn" className="h-9 text-[12.5px]">
                Cancel
              </Button>
              <Button
                variant="outline"
                disabled={submitting}
                onClick={() => submit(false)}
                data-testid="sms-consent-no-btn"
                className="h-9 text-[12.5px] border-red-200 text-red-600 hover:bg-red-50"
              >
                <XCircle className="h-3.5 w-3.5 mr-1.5" /> No
              </Button>
              <Button disabled={submitting} onClick={() => submit(true)} data-testid="sms-consent-yes-btn" className="h-9 text-[12.5px] bg-blue-600 hover:bg-blue-700 text-white">
                {submitting ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" />} Yes
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
